// Operator console (#/console, #/console/:eventId): the prototype's three zones over one action rail.
//   left   Queue / Parked / Sent to me (pull: claim; push: offers arrive in the live layer, Claim only with
//          routing.allowPull; supervisors keep Take)
//   center event tabs (Phase 2: my active events up to maxActiveEvents, raised marker, [ ] to switch), header,
//          the main video canvas (single / quad), the lane timeline, and the parked dock;
//          all site cameras open in the pop-out camera matrix (M, matrixLink.ts), time-synced to the player
//   right  Mission / Map / Contacts / Intel / Log
//   bottom action rail; drawers (Dispatch, Disposition, Note, Data issue, Park, Send to); hotkeys (design/README.md)
// Each tab keeps its own player state (camera, mode, playhead, selected alarm), new-alarm marks, open drawer and
// drawer drafts while you work another event. Every command sends expectedVersion; a 409 refetches the event and
// shows "Event changed, review and retry".
// Phase 3 follow-up: the agent panel refetches on the stream's `agent` message for the open event (after every tool
// call) and on its `event` messages; no polling.
// Phase 4: AI watches on the open event and my parked events (cards, dock, park bar, watch panel), the agent's
// requests for the open event (Agent tab; Y / N answer the newest), and G focuses "Tell the agent".
// Phase 5 (features.deterrence): the site's speakers and relays with live state (devices.ts), the Talk-down (T: tap
// opens the drawer, hold records push-to-talk, ptt.ts), Deterrents (R) and Notify drawers, and the pop-out video.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, callIsLive, callResult, errorText, isConflict, parkReasonLabel, type AgentPanel, type EventDetail, type EventSummary, type MissionStep, type StepStatus } from '../api.ts';
import { softphoneFor, useMarkEventInView } from '../telephony/index.ts';
import { Icon } from '../components/Icon.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { useLoad, useNow } from '../hooks.ts';
import { useLiveSignal } from '../live/bus.ts';
import { usePresence } from '../live/presence.tsx';
import { href, navigate } from '../router.ts';
import { useSession } from '../session.tsx';
import { useRefreshOn, useStreamEvent } from '../stream.tsx';
import { fmtDateTime } from '../time.ts';
import { ActionRail } from './ActionRail.tsx';
import { pendingFromPanel } from './Agent.tsx';
import { useAiWatches } from './aiWatches.ts';
import { approvalArgs, useCopilot, type PendingApproval } from '../live/copilot.tsx';
import { WatchLine } from './Watches.tsx';
import { openMatrix, openVideo, useMatrixHost, type MatrixCmd } from './matrixLink.ts';
import { DeterrentsDrawer, NotifyDrawer, ReportStatus, TalkDownDrawer } from './DeterDrawers.tsx';
import { defaultSpeakers, useEventDevices } from './devices.ts';
import { useLiveTalk } from './live-talk.ts';
import { usePushToTalk } from './ptt.ts';
import { stateHint, stateLabel } from './ai.ts';
import { ContextPanel, EmptyContext } from './Context.tsx';
import { CTX_TABS, type Ctl, type CtxTab, type DrawerSpec } from './ctl.ts';
import { DraftStore } from './drafts.ts';
import { DataIssueDrawer, DispatchDrawer, DispositionDrawer, HelpOverlay, NoteDrawer } from './Drawers.tsx';
import { ParkDrawer, SendToDrawer } from './Phase2Drawers.tsx';
import { Queue, waitingFor, type QTab } from './Queue.tsx';
import { useToaster } from './Toasts.tsx';
import { actorName, alarmMs, camShort, dur, isTyping, ms, normalizeEvent } from './util.ts';
import { atPlayhead, continueAfter, goLive, initialView, PRE_ROLL_MS, seek, selectedAlarm, suspend, togglePause, vtime, wake, type PlayMode, type View } from './view.ts';
import { EmptyWorkspace, EventTabs, ParkedDock, Workspace, type TabItem } from './Workspace.tsx';

const CTX_KEY = 'eyeqsoc.ctx';
function loadCtx(agentOn: boolean): CtxTab {
  try {
    const v = localStorage.getItem(CTX_KEY);
    if (v === 'agent' && !agentOn) return 'mission';
    if (v && (CTX_TABS as readonly string[]).includes(v)) return v as CtxTab;
  } catch {
    /* storage blocked */
  }
  return 'mission';
}

function addTo(s: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (s.has(id)) return s;
  const n = new Set(s);
  n.add(id);
  return n;
}
function delFrom(s: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (!s.has(id)) return s;
  const n = new Set(s);
  n.delete(id);
  return n;
}

/** "Parked · Police dispatched · raises in 04:12" for the read-only bar. */
function ParkedLine({ e }: { e: EventSummary }) {
  const now = useNow(1000);
  const fb = ms(e.park?.fallbackAt);
  return (
    <span>
      Parked · {parkReasonLabel(e.park?.reason)}
      {e.park?.note ? ` · ${e.park.note}` : ''}
      {fb !== null && (fb > now ? ` · raises in ${dur(fb - now)}` : ' · raising')}
    </span>
  );
}

function WaitingLine({ e, meId }: { e: EventSummary; meId: string }) {
  const now = useNow(1000);
  const w = waitingFor(e, now, meId);
  return <>{w ? ` · ${w}` : ''}</>;
}

export function ConsoleScreen({ eventId }: { eventId: string | null }) {
  const s = useSession();
  const cfg = s.config;
  const canOperate = s.has('operator');
  const isSup = s.has('supervisor', 'admin');
  const push = cfg.routing.mode === 'push';
  const claimAllowed = canOperate && (!push || cfg.routing.allowPull);
  const toast = useToaster();
  const presence = usePresence();
  const reloadPresence = presence?.reload;
  const meId = s.me.userId;
  const drafts = useRef(new DraftStore()).current;

  /* ---------- queue and my events (active + parked) ---------- */
  const queue = useLoad((sig) => api.queue(sig), []);
  const mine = useLoad((sig) => api.myEvents(sig), []);
  const reloadLists = useCallback(() => {
    queue.reload();
    mine.reload();
  }, [queue, mine]);
  useRefreshOn(['queue', 'event', 'raise', 'offer_withdrawn'], reloadLists, 350);

  const myList = useMemo(() => mine.data ?? [], [mine.data]);
  const active = useMemo(() => myList.filter((e) => e.state === 'active'), [myList]);
  const parked = useMemo(() => myList.filter((e) => e.state === 'parked'), [myList]);
  const activeCount = active.length;
  const atCap = activeCount >= cfg.maxActiveEvents;

  // Stable tab order: events keep their place; new ones join on the right.
  const orderRef = useRef<string[]>([]);
  const tabEvents = useMemo(() => {
    const ids = active.map((e) => e.id);
    const kept = orderRef.current.filter((id) => ids.includes(id));
    orderRef.current = [...kept, ...ids.filter((id) => !kept.includes(id))];
    return orderRef.current.map((id) => active.find((e) => e.id === id)).filter((e): e is EventSummary => !!e);
  }, [active]);
  const currentId = eventId ?? tabEvents[0]?.id ?? null;

  /* ---------- the open event (cached per tab so switching is instant) ---------- */
  const cache = useRef(new Map<string, EventDetail>());
  const evLoad = useLoad<EventDetail | null>((sig) => (currentId ? api.event(currentId, sig).then(normalizeEvent) : Promise.resolve(null)), [currentId]);
  useEffect(() => {
    if (evLoad.data) cache.current.set(evLoad.data.id, evLoad.data);
  }, [evLoad.data]);
  const ev = evLoad.data && evLoad.data.id === currentId ? evLoad.data : currentId ? (cache.current.get(currentId) ?? null) : null;
  const evRef = useRef(ev);
  evRef.current = ev;
  const reloadEvent = evLoad.reload;
  const setEvent = useCallback(
    (d: EventDetail) => {
      const n = normalizeEvent(d);
      cache.current.set(n.id, n);
      if (n.id === evRef.current?.id) evLoad.setData(n);
    },
    [evLoad],
  );

  // Live updates: an `event` message for the open event refetches its detail. Call state changes and
  // video watch lines arrive without a version bump, so every message for the open event counts.
  const sseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const soonReload = useCallback(
    (delay = 200) => {
      if (sseTimer.current) clearTimeout(sseTimer.current);
      sseTimer.current = setTimeout(reloadEvent, delay);
    },
    [reloadEvent],
  );
  /* ---------- Phase 3: the agent panel for the open event (cached per tab) ---------- */
  const agentOn = cfg.features.agent;
  const agentCache = useRef(new Map<string, AgentPanel | null>());
  const agentLoad = useLoad<AgentPanel | null>(
    (sig) =>
      agentOn && currentId
        ? api.agentPanel(currentId, sig).catch((e: unknown) => {
            // No run for this event (before Phase 3, or the agent never saw it).
            if (e instanceof ApiError && e.status === 404) return null;
            throw e;
          })
        : Promise.resolve(null),
    [agentOn, currentId],
  );
  useEffect(() => {
    if (currentId && agentLoad.data !== undefined) agentCache.current.set(currentId, agentLoad.data);
  }, [agentLoad.data, currentId]);
  const agentData = agentLoad.data !== undefined ? agentLoad.data : currentId ? agentCache.current.get(currentId) : undefined;
  const reloadAgent = agentLoad.reload;
  const agentTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const soonAgent = useCallback(
    (delay = 300) => {
      if (!agentOn) return;
      if (agentTimer.current) clearTimeout(agentTimer.current);
      agentTimer.current = setTimeout(reloadAgent, delay);
    },
    [agentOn, reloadAgent],
  );
  useStreamEvent('event', (sum) => {
    const cur = evRef.current;
    if (!cur || sum.id !== cur.id) return;
    soonReload();
    // Instructions, approvals and watches change the event too.
    soonAgent(400);
  });
  // After every agent tool call (eventId only): the live trace.
  useStreamEvent('agent', (m) => {
    if (m?.eventId && m.eventId === evRef.current?.id) soonAgent();
  });
  useStreamEvent('agent_reply', (m) => {
    if (m?.eventId && m.eventId === evRef.current?.id) soonAgent(100);
  });
  useEffect(
    () => () => {
      if (sseTimer.current) clearTimeout(sseTimer.current);
      if (agentTimer.current) clearTimeout(agentTimer.current);
    },
    [],
  );

  // Telephony: connect the browser softphone (the call panel does too, on every screen), and refetch the event when it
  // reports a call starting or ending (the server learns it from the provider's notifications).
  const softphone = softphoneFor(cfg);
  useEffect(() => {
    if (!softphone) return;
    softphone.load();
    return softphone.on((e) => {
      if (e.type === 'started' || e.type === 'ended') soonReload(1500);
    });
  }, [softphone, soonReload]);

  // Safety net while a call is live or its recording is processing: poll in case a stream message is missed.
  const callActive = !!ev && ev.actions.some((a) => a.type === 'call' && (callIsLive(callResult(a)) || callResult(a).recording?.status === 'pending'));
  useEffect(() => {
    if (!callActive) return;
    const t = setInterval(reloadEvent, 4000);
    return () => clearInterval(t);
  }, [callActive, reloadEvent]);

  const canAct = !!ev && ev.state === 'active' && ev.owner?.id === meId;
  const evId = ev?.id ?? null;

  /* ---------- Phase 5: devices, the chosen speakers, push-to-talk ---------- */
  const deterOn = cfg.features.deterrence;
  const devices = useEventDevices(ev, deterOn);
  const [spkSel, setSpkSel] = useState<Record<string, string[]>>({});
  const clipPtt = usePushToTalk(
    (r, t) => {
      // Played everywhere: the on-air bar and the event log already say so. Only a speaker that missed it toasts.
      const bad = r.devices.filter((d) => !d.ok).length;
      if (bad)
        toast({
          icon: 'mic',
          tone: 'warn',
          title: `Push-to-talk not played on ${bad} of ${r.devices.length}`,
          body: `${t.label}${r.devices.some((d) => d.simulated) ? ' · simulated speakers' : ''}. The recording is stored with the event.`,
        });
      if (t.eventId === evRef.current?.id) soonReload(100);
    },
    (msg) => toast({ tone: 'warn', icon: 'mic', title: 'Push-to-talk', body: msg }),
  );
  const liveTalk = useLiveTalk(
    (t) => {
      if (t.eventId === evRef.current?.id) soonReload(100);
    },
    (msg) => toast({ tone: 'warn', icon: 'mic', title: 'Live talk', body: msg }),
  );

  /* ---------- Phase 4: AI watches, the agent's requests, the command box ---------- */
  const watchIds = useMemo(() => [...(currentId ? [currentId] : []), ...parked.map((e) => e.id)], [currentId, parked]);
  const aiWatches = useAiWatches(watchIds, cfg.features.agent);
  const copilot = useCopilot();
  const setOpenEvent = copilot?.setOpenEvent;
  const learn = copilot?.learn;
  useEffect(() => {
    setOpenEvent?.(currentId);
    return () => setOpenEvent?.(null);
  }, [currentId, setOpenEvent]);
  useEffect(() => {
    if (learn) learn([...myList, ...(queue.data ?? [])]);
  }, [learn, myList, queue.data]);
  const evApprovals = useMemo<PendingApproval[]>(() => {
    if (!evId) return [];
    const fromCop = (copilot?.approvals ?? []).filter((a) => a.eventId === evId);
    const panel = agentData && agentData.visible ? pendingFromPanel(agentData) : [];
    const extra = panel
      .filter((a) => !fromCop.some((x) => x.id === a.id))
      .map((a) => ({ id: a.id, eventId: a.eventId, tool: a.tool, summary: a.summary, reason: a.reason, expiresAt: a.expiresAt, requestedAt: a.requestedAt, args: approvalArgs(a.evidence) }));
    return [...fromCop, ...extra].sort((a, b) => (Date.parse(a.requestedAt ?? '') || 0) - (Date.parse(b.requestedAt ?? '') || 0));
  }, [copilot?.approvals, agentData, evId]);
  const [cmdFocus, setCmdFocus] = useState(0);

  /* ---------- changes to my tabs I did not make (reassign, supervisor take) ---------- */
  const expected = useRef(new Set<string>());
  const prevActive = useRef<Set<string> | null>(null);
  const [raisedLocal, setRaisedLocal] = useState<ReadonlySet<string>>(new Set());
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set());
  const [raiseSeq, setRaiseSeq] = useState(0);
  useEffect(() => {
    if (!mine.data) return;
    const now = new Set(active.map((e) => e.id));
    const prev = prevActive.current;
    prevActive.current = now;
    if (!prev) return;
    // Events that arrive in or leave the tabs show there; no pop-up (2026-10-28: pop-ups only for actions that didn't
    // go through). One that left is fetched so its cached state is current (closed elsewhere, taken, reassigned, sent).
    for (const e of active) {
      if (prev.has(e.id)) continue;
      expected.current.delete(e.id);
    }
    for (const id of prev) {
      if (now.has(id) || expected.current.delete(id) || parked.some((p) => p.id === id)) continue;
      api.event(id).then(
        (d) => cache.current.set(d.id, normalizeEvent(d)),
        () => undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine.data]);

  /* ---------- raised (pulses until opened) ---------- */
  useLiveSignal((sig) => {
    if (sig.type === 'accepted') {
      expected.current.add(sig.detail.id);
      const n = normalizeEvent(sig.detail);
      cache.current.set(n.id, n);
      reloadLists();
    } else if (sig.type === 'mine-changed') {
      reloadLists();
    } else if (sig.type === 'assigned') {
      expected.current.add(sig.eventId);
    } else if (sig.type === 'raised') {
      setOpened((o) => delFrom(o, sig.eventId));
      setRaisedLocal((r) => addTo(r, sig.eventId));
      setRaiseSeq((n) => n + 1);
      cache.current.delete(sig.eventId);
    }
  });
  const isRaised = useCallback((e: EventSummary) => (e.raised === true || raisedLocal.has(e.id)) && !opened.has(e.id), [raisedLocal, opened]);
  useEffect(() => {
    if (!currentId) return;
    setRaisedLocal((r) => delFrom(r, currentId));
    setOpened((o) => addTo(o, currentId));
  }, [currentId, raiseSeq]);

  /* ---------- new alarms (per event) ---------- */
  const [newIdsMap, setNewIdsMap] = useState<Record<string, ReadonlySet<string>>>({});
  const seenAlarms = useRef(new Map<string, Set<string>>());
  useEffect(() => {
    if (!ev) return;
    const prev = seenAlarms.current.get(ev.id);
    if (prev) {
      const fresh = ev.alarms.filter((a) => !prev.has(a.id));
      if (fresh.length) {
        setNewIdsMap((m) => ({ ...m, [ev.id]: new Set([...(m[ev.id] ?? []), ...fresh.map((a) => a.id)]) }));
      }
    }
    seenAlarms.current.set(ev.id, new Set(ev.alarms.map((a) => a.id)));
  }, [ev, toast]);
  const newIds = useMemo<ReadonlySet<string>>(() => (evId ? (newIdsMap[evId] ?? new Set()) : new Set()), [newIdsMap, evId]);

  /* ---------- view (player), one per event ---------- */
  const [views, setViews] = useState<Record<string, View>>({});
  const [shownEv, setShownEv] = useState<string | null>(null);
  if (shownEv !== evId) {
    // Switching tabs: freeze the one we leave, resume (with fresh sessions) the one we come back to.
    const prev = shownEv;
    const cur = ev;
    setShownEv(evId);
    setViews((vs) => {
      const now = Date.now();
      const n = { ...vs };
      const pv = prev ? n[prev] : undefined;
      if (prev && pv) n[prev] = suspend(pv, now);
      if (cur) {
        const cv = n[cur.id];
        n[cur.id] = cv ? wake(cv, now) : initialView(cur, now);
      }
      return n;
    });
  }
  const view = evId ? (views[evId] ?? null) : null;
  const update = useCallback(
    (fn: (v: View) => View) =>
      setViews((vs) => {
        if (!evId) return vs;
        const base = vs[evId];
        return base ? { ...vs, [evId]: fn(base) } : vs;
      }),
    [evId],
  );

  /* ---------- UI state ---------- */
  const [ctx, setCtxState] = useState<CtxTab>(() => loadCtx(cfg.features.agent));
  const setCtx = useCallback((t: CtxTab) => {
    setCtxState(t);
    try {
      localStorage.setItem(CTX_KEY, t);
    } catch {
      /* ignore */
    }
  }, []);
  const [drawers, setDrawers] = useState<Record<string, DrawerSpec | null>>({});
  const drawer = evId ? (drawers[evId] ?? null) : null;
  const setDrawer = useCallback(
    (d: DrawerSpec | null) => {
      if (evId) setDrawers((m) => ({ ...m, [evId]: d }));
    },
    [evId],
  );
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [qtab, setQtab] = useState<QTab>('queue');
  useEffect(() => setConflict(false), [evId]);
  const tKey = useRef(false);

  /* ---------- commands ---------- */
  const run: Ctl['run'] = useCallback(
    async (label, fn, apply, onError) => {
      const cur = evRef.current;
      if (!cur) return undefined;
      setBusy(label);
      try {
        const r = await fn(cur.version);
        const d = apply ? apply(r) : null;
        if (d) setEvent(d);
        setConflict(false);
        return r;
      } catch (e) {
        if (onError?.(e)) return undefined;
        if (isConflict(e)) {
          setConflict(true);
          evLoad.reload();
          return undefined;
        }
        toast({ tone: 'bad', title: `${label} failed`, body: errorText(e) });
        return undefined;
      } finally {
        setBusy(null);
      }
    },
    [evLoad, toast, setEvent],
  );

  /** Marks an alarm reviewed. Opening its clip bumps the version too, so a 409 retries once with a fresh copy. */
  const markReviewed = useCallback(
    async (alarmId: string) => {
      const cur = evRef.current;
      if (!cur) return;
      try {
        setEvent(await api.reviewAlarm(cur.id, alarmId, 'reviewed', cur.version));
      } catch (e) {
        if (!isConflict(e)) {
          toast({ tone: 'bad', title: 'Could not mark the alarm reviewed', body: errorText(e) });
          return;
        }
        try {
          const fresh = await api.event(cur.id);
          const a = fresh.alarms.find((x) => x.id === alarmId);
          if (!a || a.reviewStatus !== 'new') {
            setEvent(fresh);
            return;
          }
          setEvent(await api.reviewAlarm(cur.id, alarmId, 'reviewed', fresh.version));
        } catch (e2) {
          if (isConflict(e2)) {
            setConflict(true);
            evLoad.reload();
          } else toast({ tone: 'bad', title: 'Could not mark the alarm reviewed', body: errorText(e2) });
        }
      }
    },
    [evLoad, toast, setEvent],
  );

  const selectAlarm = useCallback(
    (id: string) => {
      const cur = evRef.current;
      const a = cur?.alarms.find((x) => x.id === id);
      if (!cur || !a) return;
      const now = Date.now();
      update((v) => ({ ...seek({ ...v, cameraId: a.cameraId ?? v.cameraId, alarmId: id }, alarmMs(a) - PRE_ROLL_MS, now, 'alarm'), synced: false }));
      setNewIdsMap((m) => {
        const set = m[cur.id];
        return set && set.has(id) ? { ...m, [cur.id]: delFrom(set, id) } : m;
      });
      if (a.reviewStatus === 'new' && cur.state === 'active' && cur.owner?.id === meId) void markReviewed(id);
    },
    [update, markReviewed, meId],
  );

  /* ---------- tabs ---------- */
  const openTab = useCallback((id: string) => navigate(href.console(id)), []);
  const cycleTab = (dir: 1 | -1) => {
    const ids = tabEvents.map((e) => e.id);
    if (!ids.length) return;
    const i = currentId ? ids.indexOf(currentId) : -1;
    const next = i < 0 ? (dir === 1 ? ids[0] : ids[ids.length - 1]) : ids[(i + dir + ids.length) % ids.length];
    if (next && next !== currentId) openTab(next);
  };
  /** After an event leaves my tabs: the next tab, or the empty console. */
  const showNextTab = useCallback(
    (leavingId: string) => {
      const ids = orderRef.current;
      const i = ids.indexOf(leavingId);
      const rest = ids.filter((x) => x !== leavingId);
      const next = rest[Math.min(Math.max(0, i), rest.length - 1)] ?? null;
      navigate(href.console(next), { replace: true });
    },
    [],
  );

  /* ---------- claim / take / resume ---------- */
  const claim = async (e: EventSummary) => {
    if (!claimAllowed) return;
    if (atCap) {
      toast({ tone: 'warn', title: `You have ${activeCount}/${cfg.maxActiveEvents} active`, body: `${cfg.features.park ? 'Park or close' : 'Close'} an event to claim the next one.` });
      return;
    }
    setBusyId(e.id);
    expected.current.add(e.id);
    try {
      const d = await api.claim(e.id, e.version);
      setEvent(d);
      cache.current.set(d.id, normalizeEvent(d));
      navigate(href.console(d.id));
      reloadLists();
      reloadPresence?.();
    } catch (err) {
      expected.current.delete(e.id);
      toast({ tone: 'bad', title: isConflict(err) ? 'Could not claim: it changed or someone else took it' : 'Could not claim', body: errorText(err) });
      reloadLists();
    } finally {
      setBusyId(null);
    }
  };

  const takeEvent = async (e: { id: string; version: number; displayNo: string }) => {
    setBusyId(e.id);
    expected.current.add(e.id);
    try {
      const d = await api.take(e.id, e.version);
      cache.current.set(d.id, normalizeEvent(d));
      setEvent(d);
      navigate(href.console(d.id));
      reloadLists();
    } catch (err) {
      expected.current.delete(e.id);
      if (isConflict(err)) {
        setConflict(true);
        evLoad.reload();
      }
      toast({ tone: 'bad', title: `Could not take ${e.displayNo}`, body: errorText(err) });
      reloadLists();
    } finally {
      setBusyId(null);
    }
  };

  const resume = async (e: EventSummary) => {
    if (atCap) {
      toast({ tone: 'warn', title: `At your cap (${activeCount}/${cfg.maxActiveEvents})`, body: 'Park or close an active event first. Parked events do not count.' });
      return;
    }
    setBusyId(e.id);
    expected.current.add(e.id);
    try {
      let d: EventDetail;
      try {
        d = await api.resume(e.id, e.version);
      } catch (err) {
        if (!isConflict(err)) throw err;
        // The summary was behind the event (a new alarm, a watch line): retry once with the fresh version.
        const fresh = await api.event(e.id);
        d = await api.resume(e.id, fresh.version);
      }
      cache.current.set(d.id, normalizeEvent(d));
      setEvent(d);
      setOpened((o) => addTo(o, d.id));
      navigate(href.console(d.id));
      reloadLists();
      reloadPresence?.();
    } catch (err) {
      expected.current.delete(e.id);
      toast({ tone: 'bad', title: `Could not resume ${e.displayNo}`, body: errorText(err) });
      reloadLists();
    } finally {
      setBusyId(null);
    }
  };

  const openCard = (e: EventSummary) => {
    if (e.id === currentId) return;
    if (!push && claimAllowed && !atCap && (e.state === 'queued' || e.state === 'transferred')) {
      void claim(e);
      return;
    }
    if (isSup || e.owner?.id === meId) {
      navigate(href.console(e.id));
      return;
    }
    if (!push && atCap) toast({ tone: 'warn', title: `You have ${activeCount}/${cfg.maxActiveEvents} active`, body: `${cfg.features.park ? 'Park or close' : 'Close'} an event to claim the next one.` });
  };

  /* ---------- the controller ---------- */
  const railCams = useMemo(() => (ev ? ev.cameras.slice().sort((a, b) => a.distanceRank - b.distanceRank) : []), [ev]);

  const afterLeave = (id: string) => {
    expected.current.add(id);
    const d = cache.current.get(id);
    // Show the change at once; the reload confirms it.
    mine.setData((l) => {
      if (!l) return l;
      if (d?.state === 'parked') return l.map((x) => (x.id === id ? { ...x, state: 'parked' as const, version: d.version, park: d.park ?? x.park ?? null } : x));
      return l.filter((x) => x.id !== id);
    });
    setDrawers((m) => ({ ...m, [id]: null }));
    showNextTab(id);
    reloadLists();
    reloadPresence?.();
  };

  const chosen = ev ? (spkSel[ev.id] ?? []).filter((id) => devices.byId.get(id)?.kind === 'speaker') : [];
  const speakers = ev && view ? (chosen.length ? chosen : defaultSpeakers(devices.speakers, ev, view.cameraId)) : [];
  // No speaker devices at the site: holding T talks live through the camera on screen (its own speaker, through NX).
  const mainCam = ev && view ? ev.cameras.find((x) => x.id === view.cameraId) : undefined;
  const liveMode = !!ev && deterOn && speakers.length === 0 && !!mainCam;
  const ptt = liveTalk.phase !== 'idle' ? liveTalk : clipPtt.phase !== 'idle' ? clipPtt : liveMode ? liveTalk : clipPtt;
  const pttPress = () => {
    if (!ev || !deterOn) return;
    if (!canAct) {
      toast({ tone: 'warn', title: 'Read-only', body: ev.state === 'parked' ? 'Resume the event first.' : 'You do not own this event.' });
      return;
    }
    if (liveMode && mainCam) liveTalk.pressLive({ eventId: ev.id, cameraId: mainCam.id, label: camShort(mainCam.name) });
    else clipPtt.press({ eventId: ev.id, deviceIds: speakers, label: speakers.map((id) => devices.names.zone(id) ?? id).join(', ') });
  };

  const ctl: Ctl | null =
    ev && view
      ? {
          ev,
          meId,
          canAct,
          features: cfg.features,
          routing: cfg.routing,
          isSup,
          maxActive: cfg.maxActiveEvents,
          activeCount,
          view,
          railCams,
          newIds,
          conflict,
          busy,
          run,
          reload: reloadEvent,
          toast,
          drafts,
          afterClose: (d) => {
            setDrawer(null);
            drafts.clear(d.id);
            expected.current.add(d.id);
            mine.setData((l) => l?.filter((x) => x.id !== d.id));
            showNextTab(d.id);
            reloadLists();
            reloadPresence?.();
          },
          afterLeave,
          selectAlarm,
          nextNew: () => {
            const i = ev.alarms.findIndex((x) => x.id === view.alarmId);
            const after = [...ev.alarms.slice(i + 1), ...ev.alarms.slice(0, i + 1)];
            const a = after.find((x) => x.reviewStatus === 'new');
            if (a) selectAlarm(a.id);
          },
          openMatrix: () => openMatrix(ev.id),
          openVideo: () => openVideo(ev.id),
          devices,
          speakers,
          setSpeakers: (ids) => setSpkSel((m) => ({ ...m, [ev.id]: ids })),
          ptt,
          pttPress,
          // A camera or layout change opens new video at the playhead (atPlayhead), not back at the alarm.
          selectCamera: (id) => update((v) => ({ ...atPlayhead(v, Date.now()), cameraId: id })),
          setMode: (m: PlayMode) => {
            const now = Date.now();
            if (m === 'live') return update((v) => goLive(v, now));
            if (m === 'alarm') {
              const a = selectedAlarm(ev, view);
              if (!a) return;
              return update((v) => ({ ...seek({ ...v, cameraId: a.cameraId ?? v.cameraId }, alarmMs(a) - PRE_ROLL_MS, now, 'alarm'), synced: false }));
            }
            update((v) => (v.mode === 'live' ? seek(v, (Date.parse(ev.firstAlarmAt) || now) - 30_000, now, 'archive') : { ...v, mode: 'archive' }));
          },
          toggleSync: (on) => {
            const now = Date.now();
            const a = selectedAlarm(ev, view);
            if (!a) return;
            update((v) => (on === true || !v.synced ? { ...seek(v, alarmMs(a) - PRE_ROLL_MS, now, 'archive'), layout: 'quad', synced: true } : { ...v, synced: false }));
          },
          setLayout: (l) => update((v) => ({ ...atPlayhead(v, Date.now()), layout: l })),
          playPause: () => update((v) => togglePause({ ...v, suspended: false }, Date.now())),
          jump: (sec) => {
            const now = Date.now();
            update((v) => seek(v, vtime(v, now) + sec * 1000, now, 'archive'));
          },
          seekTo: (t) => {
            const now = Date.now();
            update((v) => seek(v, t, now, 'archive'));
          },
          renewVideo: () => update((v) => wake({ ...v, suspended: false }, Date.now())),
          videoEnded: (endedAt) => update((v) => continueAfter(v, endedAt, Date.now())),
          bookmark: () => {
            if (!canAct || !view.cameraId) return;
            const at = vtime(view, Date.now());
            const camId = view.cameraId;
            // The bookmark shows on the timeline's operator row.
            void run('Bookmark', (ver) => api.bookmark(ev.id, { cameraId: camId, at: new Date(at).toISOString() }, ver), (d) => d);
          },
          setStep: (step: MissionStep, status: StepStatus) => {
            void run('Mission step', (ver) => api.missionStep(ev.id, step.id, { status }, ver), (d) => d);
          },
          openDrawer: (d) => {
            if (!canAct) {
              toast({ tone: 'warn', title: 'Read-only', body: ev.state === 'parked' ? 'Resume the event first.' : 'You do not own this event.' });
              return;
            }
            if ((d.k === 'park' && !cfg.features.park) || (d.k === 'transfer' && !cfg.features.transfer)) return;
            if ((d.k === 'talkdown' || d.k === 'deter' || d.k === 'notify') && !deterOn) return;
            setHelp(false);
            setDrawer(d);
          },
          agent: { on: agentOn, data: agentData, error: agentLoad.error, reload: reloadAgent },
          watches: aiWatches.get(ev.id),
          reloadWatches: () => aiWatches.reload(ev.id),
          approvals: evApprovals,
          askAgent: () => {
            setCtx('agent');
            setCmdFocus((n) => n + 1);
          },
          cmdFocus,
          ctx,
          setCtx,
        }
      : null;

  /* ---------- the pop-out camera matrix: tell it where the player is, run what it asks ---------- */
  useMatrixHost(evId, view, ev?.version ?? null, (m: MatrixCmd) => {
    if (!ctl) return;
    if (m.cmd === 'camera') ctl.selectCamera(m.cameraId);
    else if (m.cmd === 'live') ctl.setMode('live');
    else if (m.cmd === 'sync') ctl.toggleSync(true);
    else if (m.cmd === 'seek') ctl.seekTo(m.at);
    else if (m.cmd === 'pause' && (ctl.view.pauseStart !== null) !== m.paused) ctl.playPause();
  });

  /* ---------- hotkeys ---------- */
  const keyRef = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyRef.current = (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
    if (e.key === 'Escape') {
      if (ptt.phase === 'recording' || ptt.phase === 'opening') {
        ptt.cancel();
        tKey.current = false;
        return;
      }
      if (help) setHelp(false);
      else if (drawer) setDrawer(null);
      return;
    }
    if (isTyping(e.target)) return;
    if (e.key === '?') {
      e.preventDefault();
      setHelp((h) => !h);
      return;
    }
    // T (design/README.md): hold for push-to-talk, a tap opens the talk-down drawer. Works with that drawer open.
    if (e.key.toLowerCase() === 't' && deterOn && ctl && !help && (!drawer || drawer.k === 'talkdown')) {
      e.preventDefault();
      if (e.repeat || tKey.current) return;
      tKey.current = true;
      ctl.pttPress();
      return;
    }
    if (drawer || help) return;
    if (e.key === '[' || e.key === ']') {
      e.preventDefault();
      cycleTab(e.key === ']' ? 1 : -1);
      return;
    }
    if (!ctl) return;
    const k = e.key.toLowerCase();
    if (/^[1-9]$/.test(k)) {
      const cam = ctl.railCams[Number(k) - 1];
      if (cam) ctl.selectCamera(cam.id);
      return;
    }
    switch (k) {
      case ' ':
        e.preventDefault();
        ctl.playPause();
        return;
      case 'a':
        ctl.setMode('alarm');
        return;
      case 'l':
        ctl.setMode('live');
        return;
      case 's':
        ctl.toggleSync();
        return;
      case 'q':
        ctl.setLayout(ctl.view.layout === 'quad' ? 'single' : 'quad');
        return;
      case 'j':
        ctl.nextNew();
        return;
      case 'm':
        e.preventDefault();
        ctl.openMatrix();
        return;
      case 'k':
        ctl.setCtx('contacts');
        return;
      case 'd':
        e.preventDefault();
        ctl.openDrawer({ k: 'dispatch' });
        return;
      case 'c':
        e.preventDefault();
        ctl.openDrawer({ k: 'disposition' });
        return;
      case 'n': {
        // One meaning per key (design/README.md): N only ever denies the agent's newest request.
        const ask = ctl.approvals[ctl.approvals.length - 1];
        if (!ask || !copilot) return;
        e.preventDefault();
        void copilot.decide(ask, 'deny').then(() => ctl.agent.reload());
        return;
      }
      case 'o':
        e.preventDefault();
        ctl.openDrawer({ k: 'note' });
        return;
      case 'y': {
        const ask = ctl.approvals[ctl.approvals.length - 1];
        if (!ask || !copilot) return;
        e.preventDefault();
        void copilot.decide(ask, 'approve').then(() => ctl.agent.reload());
        return;
      }
      case 'p':
        if (!cfg.features.park) return;
        e.preventDefault();
        ctl.openDrawer({ k: 'park' });
        return;
      case 'x':
        if (!cfg.features.transfer) return;
        e.preventDefault();
        ctl.openDrawer({ k: 'transfer' });
        return;
      case 'b':
        ctl.bookmark();
        return;
      case 'r':
        if (!deterOn) return;
        e.preventDefault();
        ctl.openDrawer({ k: 'deter' });
        return;
      case 'g':
        if (!cfg.features.agent) return;
        e.preventDefault();
        ctl.askAgent();
        return;
    }
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => keyRef.current(e);
    document.addEventListener('keydown', on);
    return () => document.removeEventListener('keydown', on);
  }, []);
  // Releasing T sends the clip; a tap (nothing recorded) opens the talk-down drawer. Losing focus counts as a release.
  const upRef = useRef<() => void>(() => undefined);
  upRef.current = () => {
    if (!tKey.current) return;
    tKey.current = false;
    if (ptt.release() === 'tap' && ctl && drawer?.k !== 'talkdown') ctl.openDrawer({ k: 'talkdown' });
  };
  useEffect(() => {
    const up = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 't') upRef.current();
    };
    const blur = () => upRef.current();
    document.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      document.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  /* ---------- render ---------- */
  const readOnlyBar = (() => {
    if (!ev || canAct) return null;
    if (ev.state === 'closed' || ev.state === 'dispositioned') {
      return (
        <div className="robar">
          <Icon name="lock" />
          <span>
            Closed{ev.closedAt ? ` ${fmtDateTime(ev.closedAt, ev.siteTimeZone)}` : ''}
            {ev.disposition ? ` · ${ev.disposition.label} by ${actorName(ev.disposition.setBy)}` : ''}. Read-only.
          </span>
          {deterOn && <ReportStatus status={ev.disposition?.customerReportStatus} log={ev.log} />}
        </div>
      );
    }
    const mineParked = ev.state === 'parked' && ev.owner?.id === meId;
    if (mineParked) {
      return (
        <div className="robar parkbar">
          <Icon name="park" />
          <ParkedLine e={ev} />
          {cfg.features.agent && <WatchLine ws={aiWatches.get(ev.id)} frame={false} />}
          <span className="sp" />
          <button type="button" className="btn pri" onClick={() => void resume(ev)} disabled={!!busy || busyId === ev.id || atCap} title={atCap ? 'At your active cap' : 'Take it back as an active event'}>
            Resume
          </button>
        </div>
      );
    }
    const owned = ev.owner && ev.owner.id !== meId;
    const waiting = ev.state === 'queued' || ev.state === 'transferred';
    return (
      <div className="robar">
        <Icon name="lock" />
        <span>
          Read-only · {owned ? `${ev.owner?.name} has this event${ev.state === 'parked' ? ' (parked)' : ''}` : waiting ? (ev.state === 'transferred' ? 'sent, waiting to be accepted' : 'waiting in the queue') : (stateHint(ev.state) ?? stateLabel(ev.state).toLowerCase())}
          {waiting && <WaitingLine e={ev} meId={meId} />}
          {ev.transferNote ? ` · “${ev.transferNote}”` : ''}
        </span>
        <span className="sp" />
        {waiting && claimAllowed && (
          <button type="button" className="btn pri" onClick={() => void claim(ev)} disabled={!!busy || busyId === ev.id || atCap} title={atCap ? 'At your active cap' : undefined}>
            Claim
          </button>
        )}
        {isSup && (waiting || (ev.state === 'active' && owned)) && (
          <button type="button" className="btn" onClick={() => void takeEvent(ev)} disabled={!!busy || busyId === ev.id}>
            <Icon name="users" />
            Take
          </button>
        )}
        {isSup && (ev.state === 'agent_working' || ev.state === 'settling') && (
          <button
            type="button"
            className="btn"
            onClick={() => void takeEvent(ev)}
            disabled={!!busy || busyId === ev.id}
            title={ev.state === 'settling' ? 'Overrides the auto-resolve: the event becomes yours' : 'Take it from the AI agent: the event becomes yours'}
          >
            <Icon name="users" />
            Take from the AI
          </button>
        )}
      </div>
    );
  })();

  const conflictBar = conflict ? (
    <div className="conflictbar" role="alert">
      <Icon name="alert" />
      <span>
        <b>Event changed, review and retry.</b> Someone or something updated this event; the latest version is loaded.
      </span>
      <button type="button" className="btn sm" onClick={() => setConflict(false)}>
        OK
      </button>
    </div>
  ) : null;

  let center;
  if (ev && ctl) {
    center = (
      <Workspace
        key={ev.id}
        c={ctl}
        banner={
          <>
            <EventOpen />
            {conflictBar}
            {readOnlyBar}
          </>
        }
      />
    );
  } else if (currentId && evLoad.error) {
    center = (
      <EmptyWorkspace>
        <ErrorBox error={evLoad.error} onRetry={evLoad.reload} title="Could not load the event" />
        <a className="btn" href={href.console()}>
          Back to the queue
        </a>
      </EmptyWorkspace>
    );
  } else if (currentId || (!eventId && mine.loading && !mine.data)) {
    center = (
      <EmptyWorkspace>
        <Loading label="Loading event…" />
      </EmptyWorkspace>
    );
  } else {
    const p = presence?.me;
    center = (
      <EmptyWorkspace>
        <div>
          <h3 className="h-sm">No event open</h3>
          <p className="muted">
            {canOperate
              ? push
                ? p && presence?.shown !== 'available'
                  ? `You are ${presence?.shown ?? p.status}. Set yourself Available (top bar) to get offers.`
                  : 'Waiting for the next offer. It pops up here with a chime.'
                : 'Pick an event from the queue to claim it.'
              : isSup
                ? 'Open any queue card to view it read-only, or use the Supervisor wallboard.'
                : 'Your role cannot claim events.'}
          </p>
          {parked.length > 0 && <p className="note">You have {parked.length} parked. They come back on their own; Resume takes one back now.</p>}
          {mine.error ? <ErrorBox error={mine.error} onRetry={mine.reload} title="Could not load your events" /> : null}
        </div>
      </EmptyWorkspace>
    );
  }

  const myGroupIds = new Set((presence?.me?.groups ?? []).map((g) => g.id));
  const sentToMe = (queue.data ?? []).filter((e) => {
    const t = e.target;
    if (!t) return false;
    return t.userId === meId || (!!t.groupId && myGroupIds.has(t.groupId));
  });

  const tabItems: TabItem[] = tabEvents.map((e) => {
    const seen = seenAlarms.current.get(e.id);
    return { e, raised: isRaised(e), newAlarms: seen && e.id !== currentId ? Math.max(0, e.alarmCount - seen.size) : 0 };
  });
  const viewing = ev && !tabEvents.some((t) => t.id === ev.id) ? ev : null;

  return (
    <div className="console">
      <Queue
        items={queue.data}
        error={queue.error}
        reload={queue.reload}
        features={cfg.features}
        routing={cfg.routing}
        activeCount={activeCount}
        maxActive={cfg.maxActiveEvents}
        canClaim={claimAllowed && !atCap}
        isOperator={canOperate}
        isSup={isSup}
        openId={currentId}
        busyId={busyId}
        parked={parked}
        watchesOf={cfg.features.agent ? aiWatches.get : undefined}
        sentToMe={sentToMe}
        meId={meId}
        onOpen={openCard}
        onClaim={(e) => void claim(e)}
        onTake={(e) => void takeEvent(e)}
        onResume={(e) => void resume(e)}
        tab={qtab}
        setTab={setQtab}
      />
      <section className="zw" aria-label="Event workspace">
        <div className="evtabs-wrap">
          <EventTabs
            tabs={tabItems}
            currentId={currentId}
            viewing={viewing}
            label={currentId ? 'Loading…' : 'No event'}
            parkedCount={parked.length}
            activeCount={activeCount}
            maxActive={cfg.maxActiveEvents}
            onSelect={openTab}
          />
          <button type="button" className="btn icon helpbtn" onClick={() => setHelp(true)} title="Hotkeys (?)" aria-label="Hotkeys">
            <Icon name="keyboard" />
          </button>
        </div>
        {center}
        {cfg.features.park && (
          <ParkedDock
            parked={parked}
            openId={currentId}
            canResume={!atCap}
            busyId={busyId}
            watchesOf={cfg.features.agent ? aiWatches.get : undefined}
            onOpen={(e) => navigate(href.console(e.id))}
            onResume={(e) => void resume(e)}
          />
        )}
      </section>
      {ctl ? <ContextPanel c={ctl} /> : <EmptyContext text={ev ? '' : 'Mission, map, contacts and site intel appear here when you open an event.'} />}
      <ActionRail c={ctl} />
      {ctl && drawer?.k === 'dispatch' && <DispatchDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'disposition' && <DispositionDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'note' && <NoteDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'dataIssue' && <DataIssueDrawer key={ctl.ev.id} c={ctl} prefill={drawer.prefill} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'park' && <ParkDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'transfer' && <SendToDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'talkdown' && <TalkDownDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'deter' && <DeterrentsDrawer key={ctl.ev.id} c={ctl} relayId={drawer.relayId} onClose={() => setDrawer(null)} />}
      {ctl && drawer?.k === 'notify' && <NotifyDrawer key={ctl.ev.id} c={ctl} onClose={() => setDrawer(null)} />}
      {help && <HelpOverlay deterrence={deterOn} onClose={() => setHelp(false)} />}
    </div>
  );
}

/** While an event is open the phone stays out of its way: no panel dropping over the work by itself (telephony/softphone.ts). */
function EventOpen() {
  useMarkEventInView();
  return null;
}
